'use client';

import {useState, useEffect} from 'react';
import {useLocale} from 'next-intl';
import Card from '@/components/ui/Card';

interface UserInfo {
  nom: string;
  role: string;
}

interface Projet {
  id: number;
  titre_fr: string;
  categorie: string;
  statut: string;
  budget_objectif: number;
  montant_collecte: number;
  gouvernorat?: string;
  delegation?: string;
  pourcentage_avancement?: number;
}

interface DonItem {
  id: number;
  montant: number;
  projet_id: number;
  statut: string;
  date: string | null;
  projet_nom?: string;
}

function downloadRecuFiscal(donId: number) {
  // Telecharge le recu fiscal depuis l'API backend
  const a = document.createElement('a');
  a.href = `/api/v1/dons/${donId}/recu-fiscal`;
  a.download = `Recu_Fiscal_UTSS_${donId}.txt`;
  a.click();
}

export default function EspaceDonateur() {
  const locale = useLocale();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [projets, setProjets] = useState<Projet[]>([]);
  const [dons, setDons] = useState<DonItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('utss_user');
    if (stored) {
      const u = JSON.parse(stored);
      if (u.role && u.role !== 'donateur') {
        window.location.href = `/${locale}/dashboard`;
        return;
      }
      setUser(u);

      // Load projets + dons in parallel
      Promise.all([
        fetch('/api/v1/projets/?limit=100').then(r => r.ok ? r.json() : []),
        u.email ? fetch(`/api/v1/dons/par-email?email=${encodeURIComponent(u.email)}`).then(r => r.ok ? r.json() : []) : Promise.resolve([]),
      ]).then(([projetsData, donsData]) => {
        setProjets(projetsData);
        // Enrich dons with projet names
        const enriched = donsData.map((d: DonItem) => {
          const p = projetsData.find((pr: Projet) => pr.id === d.projet_id);
          return {...d, projet_nom: p?.titre_fr || `Projet #${d.projet_id}`};
        });
        setDons(enriched);
      }).catch(() => {}).finally(() => setLoading(false));
    } else {
      window.location.href = `/${locale}/auth`;
    }
  }, [locale]);

  if (!user) return null;
  if (loading) return <div className="max-w-5xl mx-auto px-4 py-16 text-center text-muted">Chargement...</div>;

  const confirmes = dons.filter(d => d.statut === 'confirme');
  const totalDons = confirmes.reduce((s, d) => s + d.montant, 0);
  const projetsFinances = [...new Set(dons.map(d => d.projet_id))];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Bienvenue, {user.nom}</h1>
          <p className="text-muted text-sm mt-0.5">Suivi de vos contributions et projets finances</p>
        </div>
        <a href={`/${locale}/don`} style={{color: '#fff', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-2 bg-secondary hover:bg-secondary-light text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors no-underline">
          Faire un don
        </a>
      </div>

      {/* Stats compacts */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card><div className="text-center"><div className="text-2xl font-bold text-primary">{dons.length}</div><div className="text-xs text-muted">Dons</div></div></Card>
        <Card><div className="text-center"><div className="text-2xl font-bold text-success">{totalDons} TND</div><div className="text-xs text-muted">Total confirme</div></div></Card>
        <Card><div className="text-center"><div className="text-2xl font-bold text-secondary">{projetsFinances.length}</div><div className="text-xs text-muted">Projets soutenus</div></div></Card>
      </div>

      {/* Historique des dons */}
      <Card className="mb-6">
        <h2 className="text-lg font-semibold text-primary mb-4">Historique des dons</h2>
        <div className="divide-y divide-border">
          {dons.map(don => (
            <div key={don.id} className="py-3 flex items-center gap-4">
              <div className="text-xs text-muted w-20 shrink-0">{don.date}</div>
              <div className="flex-1 min-w-0">
                <a href={`/${locale}/projets/${don.projet_id}`} style={{color: '#1B5E20'}} className="text-sm font-medium hover:underline">
                  {don.projet_nom}
                </a>
              </div>
              <div className="text-sm font-bold text-foreground shrink-0">{don.montant} TND</div>
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 ${don.statut === 'confirme' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                {don.statut === 'confirme' ? 'Confirme' : 'En attente'}
              </span>
              <div className="w-24 text-center shrink-0">
                {don.statut === 'confirme' ? (
                  <button
                    onClick={() => downloadRecuFiscal(don.id)}
                    className="text-xs text-secondary font-medium hover:underline cursor-pointer"
                  >
                    Recu fiscal
                  </button>
                ) : (
                  <span className="text-xs text-muted">—</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Projets que je finance — avec avancement */}
      <Card>
        <h2 className="text-lg font-semibold text-primary mb-4">Mes projets finances</h2>
        <p className="text-xs text-muted mb-4">Suivez l'avancement des projets que vous avez soutenus.</p>
        <div className="divide-y divide-border">
          {projetsFinances.map(pid => {
            const don = dons.find(d => d.projet_id === pid)!;
            const projet = projets.find(p => p.id === pid);
            const pct = projet && projet.budget_objectif > 0 ? Math.round((projet.montant_collecte / projet.budget_objectif) * 100) : 0;
            return (
              <a key={pid} href={`/${locale}/projets/${pid}`} style={{textDecorationColor: 'transparent'}} className="flex items-center gap-4 py-3 hover:bg-surface/50 transition-colors rounded px-2 -mx-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground">{don.projet_nom}</div>
                  {projet && (
                    <div className="text-xs text-muted mt-0.5 capitalize">{projet.categorie} — {projet.gouvernorat}</div>
                  )}
                  {/* Progress bar */}
                  <div className="h-1.5 bg-border rounded-full overflow-hidden mt-2 max-w-xs">
                    <div className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-primary' : 'bg-orange-500'}`} style={{width: `${Math.min(pct, 100)}%`}} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold" style={{color: pct >= 100 ? '#2E7D32' : '#1B5E20'}}>{pct}%</div>
                  <div className="text-[10px] text-muted">financé</div>
                  {projet?.pourcentage_avancement !== undefined && projet.pourcentage_avancement > 0 && (
                    <div className="text-[10px] text-primary font-medium mt-0.5">{projet.pourcentage_avancement}% execute</div>
                  )}
                </div>
                <svg className="w-4 h-4 text-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </a>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
